import { useState, useEffect, useCallback, useMemo } from 'react';

export interface UserPosition {
    x: number;
    y: number;
    section: string;
    timestamp: number;
    heading?: number; // Direction in degrees (0-360)
    speed?: number; // Movement speed in units per second
}

export interface MovementHistory {
    positions: UserPosition[];
    maxHistoryLength: number;
}

export interface LocationUpdate {
    position: UserPosition;
    accuracy: number;
    source: 'gps' | 'manual' | 'estimated';
}

export const useUserLocation = (initialPosition?: UserPosition) => {
    const [userPosition, setUserPosition] = useState<UserPosition>(
        initialPosition || {
            x: 100,
            y: 80,
            section: 'entrance',
            timestamp: Date.now()
        }
    );
    
    const [isTracking, setIsTracking] = useState(false);
    const [accuracy, setAccuracy] = useState<number>(0);
    const [locationSource, setLocationSource] = useState<'gps' | 'manual' | 'estimated'>('estimated');
    const [movementHistory, setMovementHistory] = useState<MovementHistory>({
        positions: [],
        maxHistoryLength: 50 // Keep last 50 positions
    });
    const [lastPosition, setLastPosition] = useState<UserPosition | null>(null);
    const [trackingInterval, setTrackingInterval] = useState<NodeJS.Timeout | null>(null);

    // Store sections for position validation
    const storeSections = useMemo(() => [
        { id: 'entrance', x: 50, y: 50, width: 100, height: 60 },
        { id: 'produce', x: 200, y: 50, width: 120, height: 80 },
        { id: 'dairy', x: 50, y: 150, width: 100, height: 60 },
        { id: 'meat', x: 200, y: 150, width: 120, height: 60 },
        { id: 'bakery', x: 50, y: 250, width: 100, height: 60 },
        { id: 'frozen', x: 200, y: 250, width: 120, height: 60 },
        { id: 'checkout', x: 350, y: 50, width: 80, height: 200 }
    ], []);

    // Determine which section the user is in based on coordinates
    const getSectionFromPosition = useCallback((x: number, y: number): string => {
        for (const section of storeSections) {
            if (x >= section.x && x <= section.x + section.width &&
                y >= section.y && y <= section.y + section.height) {
                return section.id;
            }
        }
        return 'entrance'; // Default to entrance if not in any section
    }, [storeSections]);

    // Calculate movement metrics
    const calculateMovementMetrics = useCallback((currentPos: UserPosition, previousPos: UserPosition) => {
        const timeDiff = (currentPos.timestamp - previousPos.timestamp) / 1000; // seconds
        const distance = Math.sqrt(
            Math.pow(currentPos.x - previousPos.x, 2) + 
            Math.pow(currentPos.y - previousPos.y, 2)
        );
        
        const speed = timeDiff > 0 ? distance / timeDiff : 0;
        const heading = Math.atan2(currentPos.y - previousPos.y, currentPos.x - previousPos.x) * 180 / Math.PI;
        
        return { speed, heading: (heading + 360) % 360 };
    }, []);

    // Update user position
    const updatePosition = useCallback((newPosition: Partial<UserPosition>, source: 'gps' | 'manual' | 'estimated' = 'manual') => {
        const updatedPosition: UserPosition = {
            ...userPosition,
            ...newPosition,
            timestamp: Date.now()
        };

        // Ensure position is within store boundaries
        updatedPosition.x = Math.max(0, Math.min(500, updatedPosition.x));
        updatedPosition.y = Math.max(0, Math.min(400, updatedPosition.y));
        
        // Update section based on new position
        updatedPosition.section = getSectionFromPosition(updatedPosition.x, updatedPosition.y);

        // Calculate movement metrics if we have a previous position
        if (lastPosition) {
            const { speed, heading } = calculateMovementMetrics(updatedPosition, lastPosition);
            updatedPosition.speed = speed;
            updatedPosition.heading = heading;
        }

        // Update movement history
        setMovementHistory(prev => {
            const newPositions = [...prev.positions, updatedPosition];
            if (newPositions.length > prev.maxHistoryLength) {
                newPositions.shift(); // Remove oldest position
            }
            return {
                ...prev,
                positions: newPositions
            };
        });

        setLastPosition(userPosition);
        setUserPosition(updatedPosition);
        setLocationSource(source);
    }, [userPosition, getSectionFromPosition, lastPosition, calculateMovementMetrics]);

    // Move user to a specific section
    const moveToSection = useCallback((sectionId: string) => {
        const section = storeSections.find(s => s.id === sectionId);
        if (section) {
            const centerX = section.x + section.width / 2;
            const centerY = section.y + section.height / 2;
            updatePosition({ x: centerX, y: centerY, section: sectionId }, 'manual');
        }
    }, [storeSections, updatePosition]);

    // Start GPS tracking (mock implementation for now)
    const startTracking = useCallback(() => {
        // Clear any existing interval
        if (trackingInterval) {
            clearInterval(trackingInterval);
        }
        
        setIsTracking(true);
        setLocationSource('gps');
        
        // Mock GPS updates - in real implementation, this would use actual GPS
        const mockGpsInterval = setInterval(() => {
            // Simulate more realistic movement patterns with faster speed
            const movementPatterns = [
                { dx: 12, dy: 0 },   // Move right
                { dx: -12, dy: 0 },  // Move left
                { dx: 0, dy: 12 },   // Move down
                { dx: 0, dy: -12 },  // Move up
                { dx: 8, dy: 8 },    // Move diagonal
                { dx: -8, dy: -8 },  // Move diagonal opposite
                { dx: 8, dy: -8 },   // Move diagonal other way
                { dx: -8, dy: 8 },   // Move diagonal other way
            ];
            
            const pattern = movementPatterns[Math.floor(Math.random() * movementPatterns.length)];
            const deltaX = pattern.dx + (Math.random() - 0.5) * 4; // Add some randomness
            const deltaY = pattern.dy + (Math.random() - 0.5) * 4;
            
            // Get current position for calculation
            setUserPosition(currentPos => {
                const newX = Math.max(0, Math.min(500, currentPos.x + deltaX));
                const newY = Math.max(0, Math.min(400, currentPos.y + deltaY));
                
                const updatedPosition: UserPosition = {
                    ...currentPos,
                    x: newX,
                    y: newY,
                    timestamp: Date.now(),
                    section: getSectionFromPosition(newX, newY)
                };

                // Calculate movement metrics if we have a previous position
                if (lastPosition) {
                    const { speed, heading } = calculateMovementMetrics(updatedPosition, lastPosition);
                    updatedPosition.speed = speed;
                    updatedPosition.heading = heading;
                }

                // Update movement history
                setMovementHistory(prev => {
                    const newPositions = [...prev.positions, updatedPosition];
                    if (newPositions.length > prev.maxHistoryLength) {
                        newPositions.shift(); // Remove oldest position
                    }
                    return {
                        ...prev,
                        positions: newPositions
                    };
                });

                setLastPosition(currentPos);
                setAccuracy(Math.random() * 5 + 1); // 1-6 meters accuracy
                
                return updatedPosition;
            });
            
        }, 500); // Update every 0.5 seconds for faster tracking

        setTrackingInterval(mockGpsInterval);
    }, [lastPosition, calculateMovementMetrics, getSectionFromPosition, trackingInterval]);

    // Stop GPS tracking
    const stopTracking = useCallback(() => {
        setIsTracking(false);
        setLocationSource('estimated');
        
        // Clear the tracking interval
        if (trackingInterval) {
            clearInterval(trackingInterval);
            setTrackingInterval(null);
        }
    }, [trackingInterval]);



    // Cleanup effect
    useEffect(() => {
        return () => {
            if (trackingInterval) {
                clearInterval(trackingInterval);
            }
        };
    }, [trackingInterval]);

    return {
        userPosition,
        isTracking,
        accuracy,
        locationSource,
        movementHistory,
        setMovementHistory,
        updatePosition,
        moveToSection,
        startTracking,
        stopTracking,
        getSectionFromPosition
    };
};
